import { getValue, removeValue, setValue } from "@/utils/SecureLocalStorage";
import { TargetUserModule } from "@/store/modules/TargetUserModule";
import { clearViewAsBranchFilters } from "@/utils/BranchFilterStorage";

/**
 * Session-storage key of the "Sicht auf" choice. Session storage is per browser tab and survives a
 * refresh, so a new tab opens in the viewer's own view (Taimoor, 2026-10-13, ticket 2610050); the
 * key is client-prefixed by `SecureLocalStorage`.
 */
const VIEW_AS_SELECTION_KEY = "VIEW_AS_SELECTION";

interface StoredViewAsSelection {
  /** Who chose it: a choice is only ever restored for the same signed-in user. */
  viewerId: string;
  targetUserId: string;
}

/** Keeps the choice for this tab, so it survives a refresh. */
export function storeViewAsSelection(viewerId: string, targetUserId: string) {
  const selection: StoredViewAsSelection = { viewerId, targetUserId };

  setValue(VIEW_AS_SELECTION_KEY, JSON.stringify(selection));
}

/**
 * The chosen user's id kept for this tab, or `null` when there is none or it was chosen by someone
 * else than `viewerId` (a different user signed in, in the same tab, without a logout).
 */
export function readViewAsSelection(viewerId: string | null | undefined): string | null {
  const json = getValue(VIEW_AS_SELECTION_KEY);

  if (!json || !viewerId) {
    return null;
  }

  try {
    const selection = JSON.parse(json) as Partial<StoredViewAsSelection>;

    return selection.viewerId === viewerId && selection.targetUserId
      ? selection.targetUserId
      : null;
  } catch {
    // An unreadable entry is no choice; it is overwritten by the next one.
    return null;
  }
}

/**
 * Ends the choice for this tab, in memory and in session storage. Every session boundary calls this
 * (login, a change of the signed-in user, a client / shared user / group switch); a logout clears
 * session storage as a whole.
 */
export function clearViewAsSelection() {
  removeValue(VIEW_AS_SELECTION_KEY);
  clearViewAsBranchFilters();
  TargetUserModule.clear();
}
